import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Clock, Package, ArrowsClockwise, Warning, Star, CreditCard, Paperclip } from "@phosphor-icons/react";
import FileUploader from "@/components/FileUploader";
import RequirementsForm from "@/components/RequirementsForm";
import TipCard from "@/components/TipCard";
import axios from "axios";

const STATUS_LABEL = {
  pending_requirements: "Pending requirements",
  in_progress: "In progress",
  delivered: "Delivered",
  revision_requested: "Revision requested",
  completed: "Completed",
  cancelled: "Cancelled",
  in_dispute: "In dispute",
  refunded: "Refunded",
  partially_refunded: "Partially refunded",
};

const STEPS = ["pending_requirements", "in_progress", "delivered", "completed"];

/**
 * RequirementsBlock — chooses between three displays:
 *   1. Already-submitted responses (structured snapshot present) →
 *      pretty-print each question + answer (read-only).
 *   2. Already-submitted legacy free-text → render as before.
 *   3. Awaiting buyer input → either structured form (snapshot present)
 *      or legacy textarea (no snapshot).
 *
 * Legacy orders without a `requirement_snapshot` keep the original UX so
 * past purchases never break.
 */
function RequirementsBlock({ order, isBuyer, reqText, setReqText, submitLegacy, submitStructured }) {
  const snapshot = order.requirement_snapshot || [];
  const responses = order.requirement_responses || [];
  const hasStructuredTemplate = snapshot.length > 0;
  const hasStructuredAnswers = responses.length > 0;
  const hasLegacyText = !!order.requirements;
  const submittedAlready = order.status !== "pending_requirements";

  if (hasStructuredAnswers) {
    const byId = Object.fromEntries(responses.map((r) => [r.field_id, r]));
    const ordered = snapshot.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return (
      <div className="space-y-3" data-testid="structured-requirements-view">
        {ordered.map((f) => {
          const r = byId[f.id] || {};
          let answer;
          if (f.type === "multi_select") answer = (r.values || []).join(", ") || "—";
          else if (f.type === "file")
            answer = (r.files || []).length ? `${r.files.length} file(s)` : "—";
          else answer = r.value || "—";
          return (
            <div key={f.id} className="rounded-xl bg-gray-50 p-3" data-testid={`requirement-answer-${f.id}`}>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{f.prompt}</div>
              <div className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{answer}</div>
              {f.type === "file" && (r.files || []).length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {r.files.map((url, i) => (
                    <li key={i}>
                      <a href={url} target="_blank" rel="noreferrer"
                         className="text-xs text-mv-primary hover:underline break-all">
                        {url.split("/").pop()}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (hasLegacyText) {
    return (
      <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-wrap" data-testid="legacy-requirements-view">
        {order.requirements}
      </div>
    );
  }

  if (submittedAlready) {
    return <div className="text-sm text-gray-500">No requirements were captured for this order.</div>;
  }

  if (!isBuyer) {
    return <div className="text-sm text-gray-500">Awaiting buyer requirements.</div>;
  }

  if (!order.paid) {
    return <div className="text-sm text-gray-500">Complete payment to submit requirements.</div>;
  }

  if (hasStructuredTemplate) {
    return (
      <RequirementsForm snapshot={snapshot} onSubmit={submitStructured} />
    );
  }
  return (
    <div className="space-y-2" data-testid="requirements-form">
      <Textarea rows={5} placeholder="What do you need? Goals, URLs, brand, access…"
        value={reqText} onChange={(e) => setReqText(e.target.value)} />
      <Button onClick={submitLegacy} disabled={!reqText.trim()} data-testid="submit-req-btn"
        className="bg-mv-primary text-white font-bold">Submit requirements</Button>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [reqText, setReqText] = useState("");
  const [delivMsg, setDelivMsg] = useState("");
  const [delivFiles, setDelivFiles] = useState([]);
  const [revFields, setRevFields] = useState({ overall: 5, communication: 5, quality: 5, delivery: 5, body: "" });
  const [reviewed, setReviewed] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDesc, setDisputeDesc] = useState("");
  const [disputeFiles, setDisputeFiles] = useState([]);
  const [tips, setTips] = useState([]);
  const [feePct, setFeePct] = useState(17);

  const load = () => api.get(`/orders/${id}`).then(({ data }) => setOrder(data));
  const loadTips = () => api.get(`/tips/order/${id}`).then(({ data }) => setTips(data || [])).catch(() => {});
  useEffect(() => { load(); loadTips(); }, [id]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`)
      .then(({ data }) => {
        const v = Number(data?.platform_fee_percent);
        if (Number.isFinite(v) && v > 0 && v < 100) setFeePct(v);
      }).catch(() => {});
  }, []);

  // Tip-checkout return — poll the latest tip until paid/failed.
  useEffect(() => {
    const tipSession = params.get("tip_session");
    if (!tipSession) return;
    let attempts = 0;
    const tick = async () => {
      await loadTips();
      const paid = tips.find((t) => t.stripe_checkout_session_id === tipSession && t.status === "paid");
      if (paid) {
        toast.success("Tip sent — thank you!");
        setParams({}, { replace: true });
        return;
      }
      attempts += 1;
      if (attempts < 8) setTimeout(tick, 1500);
      else setParams({}, { replace: true });
    };
    tick();
    // eslint-disable-next-line
  }, [params]);

  // After Stripe checkout return, poll for payment status
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received. Submit your requirements next.");
          setParams({}, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          setParams({}, { replace: true });
          return;
        }
      } catch (e) { /* ignore */ }
      attempts += 1;
      if (attempts < 8) setTimeout(tick, 2000);
    };
    tick();
    // eslint-disable-next-line
  }, [params]);

  if (!order) return <div className="max-w-5xl mx-auto px-6 py-20 text-gray-500">Loading…</div>;
  const isBuyer = user?.id === order.buyer_id;
  const isSeller = user?.id === order.seller_id;

  const submitReq = async () => {
    try { await api.post(`/orders/${id}/requirements`, { requirements: reqText }); toast.success("Requirements submitted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitStructuredReq = async (payload) => {
    try { await api.post(`/orders/${id}/requirements`, payload); toast.success("Requirements submitted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deliver = async () => {
    try {
      await api.post(`/orders/${id}/deliver`, {
        message: delivMsg,
        files: delivFiles.map((f) => f.file_id),
      });
      toast.success("Delivered");
      setDelivMsg("");
      setDelivFiles([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approve = async () => {
    try {
      await api.post(`/orders/${id}/approve`);
      toast.success("Order completed", { description: "Payment released to seller." });
      load();
      loadTips();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const requestRev = async () => {
    try { await api.post(`/orders/${id}/request-revision`); toast.success("Revision requested"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async () => {
    try { await api.post(`/orders/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitReview = async () => {
    try {
      await api.post("/reviews", { order_id: id, ...revFields, would_hire_again: true, matched_listing: true, revisions_count: order.revisions_used });
      toast.success("Review submitted");
      setReviewed(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDispute = async () => {
    try {
      await api.post("/disputes", {
        order_id: id, reason: disputeReason, description: disputeDesc,
        files: disputeFiles.map((f) => f.file_id),
      });
      toast.success("Dispute opened — admin will review");
      setDisputeFiles([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stepIdx = STEPS.indexOf(order.status);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <Link to={user?.role === "seller" ? "/dashboard/seller/orders" : "/dashboard/buyer/orders"}
        className="text-sm text-mv-primary font-bold">← Back to orders</Link>
      <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-7 mt-3">
        <div className="flex flex-wrap items-start gap-4 sm:gap-6 justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase font-bold tracking-wider text-gray-500">Order #{order.id.slice(0, 8)}</div>
            <h1 className="font-display font-black text-xl sm:text-2xl text-mv-secondary mt-1 break-words">{order.service_title}</h1>
            <div className="text-sm text-gray-500 mt-1 capitalize">{order.package_name} package · {order.delivery_days} day delivery</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase font-bold tracking-wider text-gray-500">Total</div>
            <div className="font-display font-black text-xl sm:text-2xl text-mv-secondary">${order.total}</div>
            <span className="inline-block mt-2 px-3 py-1 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold uppercase tracking-wider">
              {STATUS_LABEL[order.status]}
            </span>
          </div>
        </div>

        {/* Progress steps - 2 cols on mobile, 4 on sm+ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-6 sm:mt-8">
          {STEPS.map((s, i) => (
            <div key={s} className={`p-2.5 sm:p-3 rounded-xl border ${i <= stepIdx ? "border-mv-primary bg-mv-primary/5" : "border-gray-200 bg-gray-50"}`}>
              <div className="text-[10px] sm:text-xs uppercase font-bold tracking-wider text-gray-500">{`Step ${i + 1}`}</div>
              <div className={`text-xs sm:text-sm font-bold mt-1 ${i <= stepIdx ? "text-mv-primary" : "text-gray-400"}`}>{STATUS_LABEL[s]}</div>
            </div>
          ))}
        </div>

        {isBuyer && !order.paid && order.status === "pending_requirements" && (
          <div className="mt-6 p-4 sm:p-5 rounded-xl bg-amber-50 border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="pay-now-banner">
            <div>
              <div className="font-bold text-amber-900">Payment pending</div>
              <div className="text-sm text-amber-800">Complete payment to start your order.</div>
            </div>
            <Button onClick={async () => {
              try {
                const { data: session } = await api.post(`/payments/checkout/order/${order.id}`, null,
                  { headers: { Origin: window.location.origin } });
                window.location.href = session.url;
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} className="bg-mv-primary text-white font-bold w-full sm:w-auto" data-testid="pay-now-btn">
              <CreditCard size={16} className="mr-1.5" weight="duotone" /> Pay ${order.total}
            </Button>
          </div>
        )}

        {/* Actions per state */}
        <div className="mt-6 sm:mt-8 grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-bold text-mv-secondary mb-2">Requirements</div>
            <RequirementsBlock
              order={order}
              isBuyer={isBuyer}
              reqText={reqText}
              setReqText={setReqText}
              submitLegacy={submitReq}
              submitStructured={submitStructuredReq}
            />
          </div>

          <div>
            <div className="text-sm font-bold text-mv-secondary mb-2">Deliveries</div>
            <div className="space-y-3">
              {order.deliveries?.map((d) => (
                <div key={d.id} className="rounded-xl bg-gray-50 p-4 text-sm">
                  <div className="text-xs text-gray-500">{new Date(d.created_at).toLocaleString()}</div>
                  <div className="mt-1 text-gray-700 whitespace-pre-wrap">{d.message}</div>
                  {d.files?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {d.files.map((fid) => (
                        <li key={fid}>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}?auth=${localStorage.getItem("mv_token")}`}
                             target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1.5 text-mv-primary font-bold hover:underline">
                            <Paperclip size={14} weight="duotone" /> Download file
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              {isSeller && ["in_progress", "revision_requested"].includes(order.status) && (
                <div className="space-y-2" data-testid="deliver-form">
                  <Textarea rows={4} placeholder="Delivery message" value={delivMsg} onChange={(e) => setDelivMsg(e.target.value)} />
                  <FileUploader folder="deliveries" value={delivFiles} onChange={setDelivFiles}
                    label="Attach deliverables" max={10} testId="delivery-uploader" />
                  <Button onClick={deliver} disabled={!delivMsg.trim()} data-testid="deliver-btn"
                    className="bg-mv-primary text-white font-bold">Mark delivered</Button>
                </div>
              )}
              {order.deliveries?.length === 0 && !isSeller && (
                <div className="text-sm text-gray-500">No deliveries yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* Buyer actions after delivery */}
        {isBuyer && (order.status === "delivered" || order.status === "completed") && (
          <TipCard
            order={order}
            sellerName={order.seller_display_name || "your seller"}
            existingTip={tips.find((t) => t.status === "paid") || null}
            feePct={feePct}
          />
        )}

        {/* Buyer actions after delivery */}
        {isBuyer && order.status === "delivered" && (
          <div className="mt-8 p-5 rounded-xl bg-mv-accent/10 border border-mv-accent/30">
            <div className="font-bold text-mv-secondary mb-2">Review delivery</div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={approve} className="bg-mv-primary text-white font-bold" data-testid="approve-btn">
                <CheckCircle size={16} className="mr-1.5" /> Approve & release payment
              </Button>
              <Button onClick={requestRev} variant="outline" className="font-bold" data-testid="request-revision-btn">
                <ArrowsClockwise size={16} className="mr-1.5" /> Request revision ({order.revisions_total - order.revisions_used} left)
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="text-rose-600 font-bold" data-testid="open-dispute-btn">
                    <Warning size={16} className="mr-1.5" /> Open dispute
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Open a dispute</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="Reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} data-testid="dispute-reason" />
                    <Textarea placeholder="Describe the issue…" value={disputeDesc} onChange={(e) => setDisputeDesc(e.target.value)} rows={5} data-testid="dispute-desc" />
                    <div>
                      <Label className="text-xs">Evidence (optional)</Label>
                      <FileUploader folder="messages" value={disputeFiles} onChange={setDisputeFiles}
                        label="Attach evidence" max={6} testId="dispute-uploader" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={openDispute} disabled={!disputeReason.trim() || !disputeDesc.trim()} className="bg-mv-secondary text-white" data-testid="dispute-submit">Submit dispute</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        )}

        {/* Review block */}
        {isBuyer && order.status === "completed" && !reviewed && (
          <div className="mt-8 p-5 rounded-xl bg-white border border-gray-200" data-testid="review-form">
            <div className="font-bold text-mv-secondary mb-3">Leave a review</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["overall", "communication", "quality", "delivery"].map((k) => (
                <div key={k}>
                  <Label className="capitalize">{k}</Label>
                  <select className="w-full mt-1 px-2 py-2 border rounded-lg" value={revFields[k]} onChange={(e) => setRevFields((f) => ({ ...f, [k]: Number(e.target.value) }))} data-testid={`review-${k}`}>
                    {[5,4,3,2,1].map((n) => <option key={n} value={n}>{`${n} star${n > 1 ? "s" : ""}`}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <Textarea className="mt-3" placeholder="What stood out?" value={revFields.body} onChange={(e) => setRevFields((f) => ({ ...f, body: e.target.value }))} rows={3} data-testid="review-body" />
            <Button onClick={submitReview} className="mt-3 bg-mv-primary text-white font-bold" data-testid="submit-review-btn">Submit review</Button>
          </div>
        )}

        {/* Cancel for pending/in_progress */}
        {(isBuyer || isSeller) && ["pending_requirements", "in_progress", "revision_requested"].includes(order.status) && (
          <div className="mt-6 text-right">
            <Button variant="outline" className="text-gray-500" onClick={cancel} data-testid="cancel-order-btn">Cancel order</Button>
          </div>
        )}
      </div>
    </div>
  );
}
